(function () {
  "use strict";

  function initInverterCalculator(root) {
    if (!root || root.dataset.initialized === "true") {
      return;
    }

    root.dataset.initialized = "true";

    const locale =
      document.documentElement.lang || "en";

    const numberFormatter =
      new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1
      });

    const RHO_COPPER = 0.0179;

    /*
      Planning current-density floor.

      This is NOT an ampacity table.
      Final conductor ampacity still has to be checked against
      conductor insulation, temperature, installation method,
      applicable marine standards and manufacturer requirements.
    */
    const PLANNING_CURRENT_DENSITY = 4;


    const STANDARD_CABLES_MM2 = [
      1.5,
      2.5,
      4,
      6,
      10,
      16,
      25,
      35,
      50,
      70,
      95,
      120,
      150,
      185,
      240
    ];


    const STANDARD_INVERTER_WATTS = [
      300,
      500,
      600,
      800,
      1000,
      1200,
      1500,
      2000,
      2500,
      3000,
      4000,
      5000,
      6000,
      8000,
      10000,
      12000,
      15000,
      20000
    ];


    const STANDARD_DC_PROTECTION_AMPS = [
      10,
      15,
      20,
      25,
      30,
      40,
      50,
      60,
      70,
      80,
      100,
      125,
      150,
      175,
      200,
      225,
      250,
      300,
      350,
      400,
      450,
      500,
      600,
      700,
      800
    ];


    const STANDARD_AC_BREAKERS_AMPS = [
      2,
      4,
      6,
      10,
      13,
      16,
      20,
      25,
      32,
      40,
      50,
      63,
      80,
      100
    ];


    const defaultFactors = {
      resistive:
        Number(root.dataset.defaultResistiveFactor) || 1,

      capacitive:
        Number(root.dataset.defaultCapacitiveFactor) || 2,

      inductive:
        Number(root.dataset.defaultInductiveFactor) || 3,

      unknown:
        Number(root.dataset.defaultUnknownFactor) || 3
    };


    const translations = {
      engineeringRequired:
        root.dataset.tEngineeringRequired || "Engineering required"
    };


    let loadIndex =
      root.querySelectorAll("[data-load]").length || 1;


    function toNumber(value) {
      const normalized =
        String(value ?? "")
          .trim()
          .replace(/\s/g, "")
          .replace(",", ".");

      const number =
        Number(normalized);

      return Number.isFinite(number) &&
        number >= 0
        ? number
        : 0;
    }


    function formatNumber(value) {
      return numberFormatter.format(value);
    }


    function formatWatts(value) {
      if (Math.abs(value) >= 1000) {
        return (
          formatNumber(value / 1000) +
          " kW"
        );
      }

      return (
        formatNumber(value) +
        " W"
      );
    }


    function formatAmps(value) {
      return (
        formatNumber(value) +
        " A"
      );
    }


    function roundUpToStandard(
      value,
      standardValues
    ) {
      for (
        let i = 0;
        i < standardValues.length;
        i += 1
      ) {
        if (value <= standardValues[i]) {
          return standardValues[i];
        }
      }

      return null;
    }


    function setOutput(
      key,
      displayValue,
      rawValue,
      unit
    ) {
      const output =
        root.querySelector(
          `[data-result="${key}"]`
        );

      if (!output) {
        return;
      }

      output.textContent =
        displayValue;

      output.value =
        displayValue;

      output.dataset.value =
        rawValue !== null &&
        rawValue !== undefined
          ? String(rawValue)
          : "";

      output.dataset.unit =
        unit || "";
    }


    function setLoadSurgeOutput(
      row,
      watts
    ) {
      const output =
        row.querySelector(
          "[data-load-surge]"
        );

      if (!output) {
        return;
      }

      const displayValue =
        formatWatts(watts);

      output.textContent =
        displayValue;

      output.value =
        displayValue;

      output.dataset.value =
        String(watts);

      output.dataset.unit =
        "W";
    }


    function calculateCable(
      current,
      voltage,
      oneWayLength,
      voltageDropPercent
    ) {
      if (
        current <= 0 ||
        voltage <= 0 ||
        oneWayLength <= 0 ||
        voltageDropPercent <= 0
      ) {
        return {
          voltageDropSection: 0,
          currentDensitySection: 0,
          requiredSection: 0,
          standardSection: 0
        };
      }

      /*
        User enters the physical one-way distance.

        Complete electrical circuit:
        positive + negative = 2 × one-way distance.
      */
      const totalCircuitLength =
        oneWayLength * 2;

      const allowedVoltageDrop =
        voltage *
        (
          voltageDropPercent /
          100
        );

      const voltageDropSection =
        (
          RHO_COPPER *
          totalCircuitLength *
          current
        ) /
        allowedVoltageDrop;


      const currentDensitySection =
        current /
        PLANNING_CURRENT_DENSITY;


      const requiredSection =
        Math.max(
          voltageDropSection,
          currentDensitySection
        );


      const standardSection =
        roundUpToStandard(
          requiredSection,
          STANDARD_CABLES_MM2
        );


      return {
        voltageDropSection,
        currentDensitySection,
        requiredSection,
        standardSection
      };
    }


    function calculate() {
      const dcVoltage =
        toNumber(
          root.querySelector(
            '[data-input="dc-voltage"]'
          )?.value
        );


      const acVoltage =
        toNumber(
          root.querySelector(
            '[data-input="ac-voltage"]'
          )?.value
        );


      const efficiencyPercent =
        toNumber(
          root.querySelector(
            '[data-input="efficiency"]'
          )?.value
        );


      const efficiency =
        efficiencyPercent > 0
          ? efficiencyPercent / 100
          : 0.9;


      const cableLength =
        toNumber(
          root.querySelector(
            '[data-input="cable-length"]'
          )?.value
        );


      const voltageDropPercent =
        toNumber(
          root.querySelector(
            '[data-input="voltage-drop"]'
          )?.value
        );


      let totalContinuousLoad = 0;

      let largestExtraStartingLoad = 0;


      root
        .querySelectorAll("[data-load]")
        .forEach((row) => {

          const watts =
            toNumber(
              row.querySelector(
                "[data-load-watts]"
              )?.value
            );


          let factor =
            toNumber(
              row.querySelector(
                "[data-load-factor]"
              )?.value
            );


          if (factor < 1) {
            factor = 1;
          }


          const surgeWatts =
            watts * factor;


          const extraStartingPower =
            Math.max(
              surgeWatts - watts,
              0
            );


          totalContinuousLoad +=
            watts;


          largestExtraStartingLoad =
            Math.max(
              largestExtraStartingLoad,
              extraStartingPower
            );


          setLoadSurgeOutput(
            row,
            surgeWatts
          );
        });


      /*
        Add 20% planning headroom
        to the total continuous AC load.
      */
      const continuousTarget =
        totalContinuousLoad *
        1.2;


      let recommendedInverter =
        totalContinuousLoad > 0
          ? roundUpToStandard(
              continuousTarget,
              STANDARD_INVERTER_WATTS
            )
          : 0;


      if (
        totalContinuousLoad > 0 &&
        recommendedInverter === null
      ) {
        recommendedInverter =
          continuousTarget;
      }


      /*
        Surge model:

        All entered appliances are assumed
        to be operating at their normal load.

        The appliance with the largest additional
        starting requirement then starts.

        This avoids unrealistically adding every
        possible starting surge simultaneously.
      */
      const requiredSurge =
        totalContinuousLoad +
        largestExtraStartingLoad;


      /*
        A required surge figure should never
        be below the recommended continuous rating.
      */
      const surgeRequirement =
        Math.max(
          requiredSurge,
          recommendedInverter || 0
        );


      /*
        DC input current is based on the recommended
        continuous inverter power.

        I = P / (V × efficiency)
      */
      let dcInputCurrent = 0;

      if (
        recommendedInverter > 0 &&
        dcVoltage > 0 &&
        efficiency > 0
      ) {
        dcInputCurrent =
          recommendedInverter /
          (
            dcVoltage *
            efficiency
          );
      }


      const cable =
        calculateCable(
          dcInputCurrent,
          dcVoltage,
          cableLength,
          voltageDropPercent
        );


      /*
        Preliminary DC protection planning target.

        Final rule still needs to respect:
        normal load current
        <= protection rating
        <= allowable conductor current

        plus inverter manufacturer requirements.
      */
      const dcProtectionTarget =
        dcInputCurrent *
        1.25;


      let dcProtection =
        dcInputCurrent > 0
          ? roundUpToStandard(
              dcProtectionTarget,
              STANDARD_DC_PROTECTION_AMPS
            )
          : 0;


      if (
        dcInputCurrent > 0 &&
        dcProtection === null
      ) {
        dcProtection =
          dcProtectionTarget;
      }


      /*
        AC output current based on
        recommended inverter continuous power.
      */
      let acCurrent = 0;

      if (
        recommendedInverter > 0 &&
        acVoltage > 0
      ) {
        acCurrent =
          recommendedInverter /
          acVoltage;
      }


      const acBreakerTarget =
        acCurrent *
        1.25;


      let acBreaker =
        acCurrent > 0
          ? roundUpToStandard(
              acBreakerTarget,
              STANDARD_AC_BREAKERS_AMPS
            )
          : 0;


      if (
        acCurrent > 0 &&
        acBreaker === null
      ) {
        acBreaker =
          acBreakerTarget;
      }


      setOutput(
        "continuous-load-watts",
        formatWatts(
          totalContinuousLoad
        ),
        totalContinuousLoad,
        "W"
      );


      setOutput(
        "recommended-inverter-watts",
        formatWatts(
          recommendedInverter || 0
        ),
        recommendedInverter || 0,
        "W"
      );


      setOutput(
        "estimated-surge-watts",
        formatWatts(
          surgeRequirement
        ),
        surgeRequirement,
        "W"
      );


      setOutput(
        "dc-input-current-amps",
        formatAmps(
          dcInputCurrent
        ),
        dcInputCurrent,
        "A"
      );


      if (
        cable.standardSection !== null &&
        cable.standardSection > 0
      ) {
        setOutput(
          "dc-cable-mm2",
          formatNumber(
            cable.standardSection
          ) + " mm²",
          cable.standardSection,
          "mm2"
        );
      } else if (
        cable.requiredSection > 0
      ) {
        setOutput(
          "dc-cable-mm2",
          translations.engineeringRequired,
          cable.requiredSection,
          "mm2"
        );
      } else {
        setOutput(
          "dc-cable-mm2",
          "0 mm²",
          0,
          "mm2"
        );
      }


      setOutput(
        "dc-protection-amps",
        formatAmps(
          dcProtection || 0
        ),
        dcProtection || 0,
        "A"
      );


      setOutput(
        "ac-output-current-amps",
        formatAmps(
          acCurrent
        ),
        acCurrent,
        "A"
      );


      setOutput(
        "ac-breaker-amps",
        formatAmps(
          acBreaker || 0
        ),
        acBreaker || 0,
        "A"
      );


      const highCurrentWarning =
        root.querySelector(
          "[data-high-current-warning]"
        );


      if (highCurrentWarning) {
        highCurrentWarning.hidden =
          dcInputCurrent <= 300;
      }


      const engineeringWarning =
        root.querySelector(
          "[data-engineering-warning]"
        );


      const engineeringRequired =
        dcInputCurrent > 600 ||
        cable.requiredSection > 240;


      if (engineeringWarning) {
        engineeringWarning.hidden =
          !engineeringRequired;
      }


      return {
        totalContinuousLoad,
        continuousTarget,
        recommendedInverter,
        requiredSurge:
          surgeRequirement,
        dcInputCurrent,
        cable,
        dcProtection,
        acCurrent,
        acBreaker
      };
    }


    function updateFactorFromType(row) {
      if (!row) {
        return;
      }


      const typeSelect =
        row.querySelector(
          "[data-load-type]"
        );


      const factorInput =
        row.querySelector(
          "[data-load-factor]"
        );


      if (
        !typeSelect ||
        !factorInput
      ) {
        return;
      }


      const type =
        typeSelect.value;


      const factor =
        defaultFactors[type] ??
        defaultFactors.unknown;


      factorInput.value =
        String(factor);
    }


    /*
      New appliance rows are cloned from the existing
      Liquid-rendered row.

      This is intentional:
      all labels, option text, placeholders and aria labels
      are therefore already translated by Shopify.

      No English UI strings need to be hard-coded in JS.
    */
    function createLoadRow() {
      const templateRow =
        root.querySelector(
          "[data-load]"
        );


      const container =
        root.querySelector(
          ".bw-inverter-load-table"
        );


      if (
        !templateRow ||
        !container
      ) {
        return;
      }


      loadIndex += 1;

      const index =
        loadIndex;


      const row =
        templateRow.cloneNode(true);


      row.dataset.loadIndex =
        String(index);


      const nameInput =
        row.querySelector(
          "[data-load-name]"
        );


      const wattsInput =
        row.querySelector(
          "[data-load-watts]"
        );


      const typeSelect =
        row.querySelector(
          "[data-load-type]"
        );


      const factorInput =
        row.querySelector(
          "[data-load-factor]"
        );


      const surgeOutput =
        row.querySelector(
          "[data-load-surge]"
        );


      /*
        Appliance name
      */
      if (nameInput) {
        nameInput.id =
          `bw-load-name-${index}`;

        nameInput.name =
          `loads[${index}][name]`;

        nameInput.value = "";
      }


      const nameLabel =
        row.querySelector(
          'label[for^="bw-load-name-"]'
        );


      if (nameLabel) {
        nameLabel.htmlFor =
          `bw-load-name-${index}`;
      }


      /*
        Rated power
      */
      if (wattsInput) {
        wattsInput.id =
          `bw-load-watts-${index}`;

        wattsInput.name =
          `loads[${index}][watts]`;

        wattsInput.value = "0";
      }


      const wattsLabel =
        row.querySelector(
          'label[for^="bw-load-watts-"]'
        );


      if (wattsLabel) {
        wattsLabel.htmlFor =
          `bw-load-watts-${index}`;
      }


      /*
        Load type
      */
      if (typeSelect) {
        typeSelect.id =
          `bw-load-type-${index}`;

        typeSelect.name =
          `loads[${index}][type]`;

        typeSelect.value =
          "unknown";
      }


      const typeLabel =
        row.querySelector(
          'label[for^="bw-load-type-"]'
        );


      if (typeLabel) {
        typeLabel.htmlFor =
          `bw-load-type-${index}`;
      }


      /*
        Start factor
      */
      if (factorInput) {
        factorInput.id =
          `bw-load-factor-${index}`;

        factorInput.name =
          `loads[${index}][start_factor]`;

        factorInput.value =
          String(
            defaultFactors.unknown
          );
      }


      const factorLabel =
        row.querySelector(
          'label[for^="bw-load-factor-"]'
        );


      if (factorLabel) {
        factorLabel.htmlFor =
          `bw-load-factor-${index}`;
      }


      /*
        Estimated surge output
      */
      if (surgeOutput) {
        surgeOutput.id =
          `bw-load-surge-${index}`;

        surgeOutput.name =
          `loads[${index}][estimated_start_watts]`;

        surgeOutput.textContent =
          "0 W";

        surgeOutput.value =
          "0 W";

        surgeOutput.dataset.value =
          "0";

        surgeOutput.dataset.unit =
          "W";
      }


      container.appendChild(row);


      calculate();


      if (nameInput) {
        nameInput.focus();
      }
    }


    function removeLoad(row) {
      if (!row) {
        return;
      }


      const allRows =
        root.querySelectorAll(
          "[data-load]"
        );


      if (allRows.length > 1) {
        row.remove();
      } else {

        const name =
          row.querySelector(
            "[data-load-name]"
          );


        const watts =
          row.querySelector(
            "[data-load-watts]"
          );


        const type =
          row.querySelector(
            "[data-load-type]"
          );


        const factor =
          row.querySelector(
            "[data-load-factor]"
          );


        if (name) {
          name.value = "";
        }


        if (watts) {
          watts.value = "0";
        }


        if (type) {
          type.value =
            "unknown";
        }


        if (factor) {
          factor.value =
            String(
              defaultFactors.unknown
            );
        }
      }


      calculate();
    }


    root.addEventListener(
      "input",
      function () {
        calculate();
      }
    );


    root.addEventListener(
      "change",
      function (event) {

        const typeSelect =
          event.target.closest(
            "[data-load-type]"
          );


        if (typeSelect) {
          const row =
            typeSelect.closest(
              "[data-load]"
            );

          updateFactorFromType(row);
        }


        calculate();
      }
    );


    root.addEventListener(
      "click",
      function (event) {

        const addButton =
          event.target.closest(
            "[data-add-load]"
          );


        if (addButton) {
          createLoadRow();
          return;
        }


        const removeButton =
          event.target.closest(
            "[data-remove-load]"
          );


        if (removeButton) {
          const row =
            removeButton.closest(
              "[data-load]"
            );

          removeLoad(row);
        }
      }
    );


    calculate();
  }


  function initAll(context) {
    const scope =
      context || document;


    if (
      scope.matches &&
      scope.matches(
        "[data-inverter-calculator]"
      )
    ) {
      initInverterCalculator(scope);
    }


    scope
      .querySelectorAll(
        "[data-inverter-calculator]"
      )
      .forEach(
        initInverterCalculator
      );
  }


  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        initAll(document);
      }
    );
  } else {
    initAll(document);
  }


  /*
    Correct Shopify Theme Editor event.
  */
  document.addEventListener(
    "shopify:section:load",
    function (event) {
      initAll(event.target);
    }
  );

})();